import React, { useState, useEffect, useCallback } from "react";
import { addMonths, startOfToday } from "date-fns";
import {
  DayPicker,
  DateRange,
  useDayPicker,
  DayProps,
  CaptionLabel,
} from "react-day-picker";
import "react-day-picker/style.css";
import { fi } from "date-fns/locale";
import { Link, useSearchParams } from "react-router-dom";
import CustomDayButton from "./CustomDayButton";
import { ReservedDate } from "../../pages/BookingsPage/bookingsSlice";
import { AccommodationListItem } from "../../pages/AccommodationsPage/accommodationsSlice";
import { current } from "@reduxjs/toolkit";
import { areDatesDateComponentsSame } from "../../common/misc";
import { Button } from "react-bootstrap";
import { useAuthUser } from "../../features/auth/authHooks";

const WEEKDAYS_LONG = {
  fi: [
    "Sunnuntai",
    "Maanantai",
    "Tiistai",
    "Keskiviikko",
    "Torstai",
    "Perjantai",
    "Lauantai",
  ],
};
const WEEKDAYS_SHORT = {
  fi: ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"],
};

const MONTHS = {
  fi: [
    "Tammikuu",
    "Helmikuu",
    "Maaliskuu",
    "Huhtikuu",
    "Toukokuu",
    "Kesäkuu",
    "Heinäkuu",
    "Elokuu",
    "Syyskuu",
    "Lokakuu",
    "Marraskuu",
    "Joulukuu",
  ],
};

const FIRST_DAY = {
  fi: 1,
};

interface ReactDayPickerWrapperProps {
  selectionMode?: "single" | "multiple" | "range";
  interval: number;
  changeMonth: (month: Date) => void;
  numberOfMonths: number;
  reservedDates: { [key: string]: ReservedDate };
  accommodationId: string;
  onRangeSelected: Function;
  allowRangeSelectOnReservedDates: boolean;
  showResetRangeButton: boolean;
  noPastDatesSelectable?: boolean;
  onIcalClick?: (reservedDate: ReservedDate) => void;
}

const ReactDayPickerWrapper: React.FC<ReactDayPickerWrapperProps> = ({
  selectionMode = "single",
  interval = 0,
  changeMonth = () => {},
  numberOfMonths = 1,
  reservedDates = {},
  accommodationId,
  onRangeSelected,
  allowRangeSelectOnReservedDates,
  showResetRangeButton,
  noPastDatesSelectable = false, // ✅ default: off
  onIcalClick,
}) => {
  const { isOwnerUser } = useAuthUser();
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [daysList, setDaysList] = useState<Date[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDateForTabCalendar = searchParams.get(
    "selectedDateForTabCalendar",
  );
  //const [reservedDates, setReservedDates] = useState<any[]>([]);
  const [selectedDates, setSelectedDates] = useState<DateRange | null>(null);

  const today = new Date();
  const nextMonth = today;
  const [selectedMonth, setSelectedMonth] = useState(
    selectedDateForTabCalendar != null
      ? new Date(selectedDateForTabCalendar)
      : nextMonth,
  );

  const doReset = () => {
    setFrom(null);
    setTo(null);
    setRange(undefined);
    setDaysList([]);
  };

  const onChangingMonthChange = (month: Date) => {
    const formattedDate = new Date(month);
    changeMonth(formattedDate);
    setSelectedMonth(formattedDate);
  };

  const handleDayClick = useCallback(
    (day: Date, modifiers: any) => {
      // Implement your logic here
    },
    [reservedDates],
  );

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is zero-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleRangeSelect = (
    selected: DateRange | undefined,
    triggerDate: Date,
  ) => {
    // IF user is maintainer -> Can't make bookings
    if (!isOwnerUser) return;
    if (selected?.from && selected.to && selected.to !== selected.from) {
      //Check that selected to and from ARE NOT AT SAME DATE (ignore time)
      if (areDatesDateComponentsSame(selected.from, selected.to)) {
        return;
      }
      if (!allowRangeSelectOnReservedDates) {
        // Selected dates are not permitted to be already reserved
        // Loop through the selected date range
        let currentDate = new Date(selected.from);
        const endDate = new Date(selected.to);
        let timeDifference = endDate.getTime() - currentDate.getTime();
        let dayDifference = timeDifference / (1000 * 3600 * 24);
        let bookingDayCounter = 0;
        // While currentDate is less than or equal to endDate
        while (currentDate <= endDate) {
          let bookingStartDate = false;
          let bookingEndDate = false;
          if (bookingDayCounter == 0) {
            bookingStartDate = true;
          }
          if (bookingDayCounter == dayDifference) {
            bookingEndDate = true;
          }
          const formattedDate = formatDate(currentDate);
          const reservedDatesCurrentDate = reservedDates[formattedDate];
          // If the current date exists in reservedDates, return false
          if (reservedDatesCurrentDate) {
            let dateOk = false;
            //Check if reservedDate is ending a booking and
            //currentDate is starting a booking, then booking is ok.
            if (reservedDatesCurrentDate.endDay && bookingStartDate) {
              dateOk = true;
            }
            //Check if reservedDate is starting a booking and
            //currentDate is ending a booking, then booking is ok.
            if (reservedDatesCurrentDate.startDay && bookingEndDate) {
              dateOk = true;
            }
            //otherwise can't make booking
            if (!dateOk) {
              return false;
            }
          }
          // Move to the next day
          currentDate.setDate(currentDate.getDate() + 1);
          bookingDayCounter++;
        }
      }
      setSelectedDates(selected);
      onRangeSelected(selected);
    }
  };
  /*
    const renderDay = (day: Date, modifiers: any) => {
      const date = day.getDate();
      const dateKey = day.toISOString().split("T")[0]; // Formatting as YYYY-MM-DD
      const currentReserved = reservedDates ? reservedDates[dateKey] : null;
  
      return (
        <div>
          {currentReserved && currentReserved.startDay ? (
            <div>
              <Link
                to={`/bookings/${currentReserved.bookingID}`}
                className={`order-type order-type-${currentReserved.bookingType}`}
              />
              {date}
            </div>
          ) : (
            <div>{date}</div>
          )}
        </div>
      );
    };
    
   */
  const reservedDatesAsDateArray: Date[] = [];
  const reservedStartDatesAsDateArray: Date[] = [];
  const reservedEndDatesAsDateArray: Date[] = [];

  if (reservedDates && Object.keys(reservedDates).length !== 0) {
    for (const [key, value] of Object.entries(reservedDates)) {
      if (value.startDay && value.endDay) {
        // console.log(key);
        // console.log(value);
        reservedDatesAsDateArray.push(new Date(key));
      } else {
        if (value.startDay) {
          reservedStartDatesAsDateArray.push(new Date(key));
          //console.log(key);
          //console.log(new Date(key));
        } else if (value.endDay) {
          reservedEndDatesAsDateArray.push(new Date(key));
        } else {
          reservedDatesAsDateArray.push(new Date(key));
        }
      }
    }
  }
  let startMonth = new Date(new Date().getFullYear() - 1, 0, 1);
  let endMonth = new Date(new Date().getFullYear() + 5, 11, 31);
  const resetRange = () => {
    setSelectedDates(undefined);
    //onRangeSelected(undefined);
  };

  const disabledDays = [];

  // Disable past days if noPastDatesSelectable is true
  if (noPastDatesSelectable) {
    const todayStart = startOfToday();
    disabledDays.push({ before: todayStart });
  }
  return (
    <div className={noPastDatesSelectable ? "no-past-selectable-calendar" : ""}>
      {showResetRangeButton && (
        <Button
          variant="primary"
          className="mt-auto w-100 text-nowrap mb-8"
          onClick={() => resetRange()}
        >
          Tyhjennä pvm valinta
        </Button>
      )}
      <DayPicker
        locale={fi}
        mode="range" //{selectionMode}
        captionLayout="dropdown"
        numberOfMonths={numberOfMonths}
        onMonthChange={onChangingMonthChange}
        onDayClick={handleDayClick}
        onSelect={handleRangeSelect}
        showOutsideDays={false}
        required={false}
        selected={selectedDates}
        month={selectedMonth}
        startMonth={startMonth}
        endMonth={endMonth}
        disabled={disabledDays}
        components={{
          DayButton: (props) => {
            const { day, modifiers, ...buttonProps } = props;
            return (
              <CustomDayButton
                day={day}
                selected={modifiers.selected}
                modifiers={modifiers}
                reservedDates={reservedDates}
                accommodationId={accommodationId}
                onIcalClick={onIcalClick}
                {...buttonProps}
              />
            );
          },
        }}
        showWeekNumber
        modifiers={{
          reserved: reservedDatesAsDateArray,
          lastDayReserved: reservedEndDatesAsDateArray,
          firstDayReserved: reservedStartDatesAsDateArray,
        }}
      />
    </div>
  );
};

export default ReactDayPickerWrapper;
